import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { COLOR_INVENTORY } from 'src/constants/colorInventory';
import { TYPOGRAPHY_INVENTORY } from 'src/constants/typographyInventory';
import { getPriceData } from 'src/service/apiService';

const ThemeSettingsContext = createContext();

const HEX_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const buildDefaultValues = () =>
  COLOR_INVENTORY.reduce((acc, item) => {
    acc[item.id] = item.defaultValue;
    return acc;
  }, {});

const buildDefaultTypographyValues = () =>
  TYPOGRAPHY_INVENTORY.reduce((acc, item) => {
    acc[item.id] = {
      fontSize: item.defaultFontSize,
      fontWeight: item.defaultFontWeight,
    };
    return acc;
  }, {});

const normalizeTypographyValue = (rawValue, fallback) => {
  const defaultValue = fallback || { fontSize: '', fontWeight: 'normal' };

  if (!rawValue) return defaultValue;

  // Allow backend to return either an object or a JSON string
  const parsedValue = (() => {
    if (typeof rawValue === 'string') {
      try {
        const maybeObject = JSON.parse(rawValue);
        if (maybeObject && typeof maybeObject === 'object') {
          return maybeObject;
        }
      } catch (err) {
        // fall through to handle non-JSON strings
      }
      return { fontSize: rawValue };
    }
    return rawValue;
  })();

  const fontSize = parsedValue?.fontSize ?? defaultValue.fontSize;
  const fontWeight = parsedValue?.fontWeight ?? defaultValue.fontWeight;

  return {
    fontSize: fontSize?.toString?.() || defaultValue.fontSize,
    fontWeight: fontWeight === 'bold' ? 'bold' : 'normal',
  };
};

const normalizeHex = (value, fallback) => {
  if (!value) return fallback;
  const trimmed = value.trim();
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  if (HEX_REGEX.test(withHash)) {
    return withHash.toUpperCase();
  }
  return fallback;
};

export const ThemeSettingsProvider = ({ children }) => {
  const defaultValues = useMemo(() => buildDefaultValues(), []);
  const defaultTypographyValues = useMemo(() => buildDefaultTypographyValues(), []);
  const [colorValues, setColorValues] = useState(defaultValues);
  const [typographyValues, setTypographyValues] = useState(defaultTypographyValues);

  const resolvedColors = useMemo(() => {
    const merged = { ...defaultValues };
    Object.entries(colorValues || {}).forEach(([key, value]) => {
      if (value) {
        merged[key] = value;
      }
    });
    return merged;
  }, [colorValues, defaultValues]);

  const resolvedTypography = useMemo(() => {
    const merged = { ...defaultTypographyValues };
    Object.entries(typographyValues || {}).forEach(([key, value]) => {
      if (value) {
        merged[key] = value;
      }
    });
    return merged;
  }, [typographyValues, defaultTypographyValues]);

  const updateColorValue = useCallback(
    (id, value) => {
      setColorValues((prev) => {
        const fallback = prev[id] || defaultValues[id];
        return {
          ...prev,
          [id]: normalizeHex(value, fallback),
        };
      });
    },
    [defaultValues]
  );

  const loadFromSettings = useCallback(
    (savedColors) => {
      if (!savedColors || typeof savedColors !== 'object') return;
      setColorValues((prev) => {
        const next = { ...prev };
        Object.entries(savedColors).forEach(([key, value]) => {
          if (defaultValues[key]) {
            next[key] = normalizeHex(value, defaultValues[key]);
          }
        });
        return next;
      });
    },
    [defaultValues]
  );

  const updateTypographyValue = useCallback(
    (id, field, value) => {
      setTypographyValues((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          [field]: value,
        },
      }));
    },
    []
  );

  const loadTypographyFromSettings = useCallback(
    (savedTypography) => {
      if (!savedTypography) return;

      const normalizedTypography =
        typeof savedTypography === 'string'
          ? (() => {
              try {
                return JSON.parse(savedTypography);
              } catch (err) {
                return null;
              }
            })()
          : savedTypography;

      if (!normalizedTypography || typeof normalizedTypography !== 'object') return;

      setTypographyValues((prev) => {
        const next = { ...prev };
        Object.entries(normalizedTypography).forEach(([key, value]) => {
          if (defaultTypographyValues[key]) {
            next[key] = normalizeTypographyValue(value, defaultTypographyValues[key]);
          }
        });
        return next;
      });
    },
    [defaultTypographyValues]
  );

  const getResolvedColor = useCallback(
    (defaultHex) => {
      if (!defaultHex) return defaultHex;
      const key = `hex-${defaultHex.replace('#', '').toLowerCase()}`;
      return resolvedColors[key] || defaultHex;
    },
    [resolvedColors]
  );

  const hydrateFromBackend = useCallback(async () => {
    try {
      const resp = await getPriceData();
      if (resp?.data?.crmTheme) {
        loadFromSettings(resp.data.crmTheme);
      }
      if (resp?.data?.crmTypography) {
        loadTypographyFromSettings(resp.data.crmTypography);
      }
    } catch (err) {
      // Silently fail - theme hydration errors are not critical
    }
  }, [loadFromSettings, loadTypographyFromSettings]);

  const value = useMemo(
    () => ({
      inventory: COLOR_INVENTORY,
      typographyInventory: TYPOGRAPHY_INVENTORY,
      colorValues,
      typographyValues,
      resolvedColors,
      resolvedTypography,
      defaultValues,
      defaultTypographyValues,
      updateColorValue,
      updateTypographyValue,
      loadFromSettings,
      loadTypographyFromSettings,
      getResolvedColor,
      hydrateFromBackend,
    }),
    [
      colorValues,
      typographyValues,
      defaultValues,
      defaultTypographyValues,
      getResolvedColor,
      loadFromSettings,
      loadTypographyFromSettings,
      resolvedColors,
      resolvedTypography,
      updateColorValue,
      updateTypographyValue,
      hydrateFromBackend,
    ]
  );

  return <ThemeSettingsContext.Provider value={value}>{children}</ThemeSettingsContext.Provider>;
};

ThemeSettingsProvider.propTypes = {
  children: PropTypes.node,
};

export const useThemeSettings = () => useContext(ThemeSettingsContext);
